import classNames from "classnames";
import { EmptyState, ListGroup, ListGroupControl, ListGroupItem, LoadingState, View } from "components/Shared";
import Pagination from "components/Shared/Tcp/Pagination";
import Datasource, { DatasourceResponse } from "lib/datasource";
import { translate } from "lib";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Link } from "react-router-dom";

/**
 * The property workspace's "Tenants" tab.
 *
 * ONE PAGE PER REQUEST, for the same reason as components/Tenancies/Collection
 * beside it (Sim, 1 Sep 2026): this tab was handed `property.tenants`, an array
 * Properties/Show fetched with `per_page: -1` on every property page view,
 * whether or not the tab was ever opened. The pair of unbounded requests is what
 * ran the API out of memory on a large property. It now owns its own request and
 * asks for one page at a time, the same arrangement as
 * components/Projects/CollectionDynamic.
 *
 * Sort and search are therefore SERVER-SIDE: `?sort=` on real columns, and `?q=`
 * (debounced) matching the same fields the in-browser filter used to —
 * V2::TenantsController::SEARCH_COLUMNS.
 */

// Columns on `tenants`. Anything that is not one sends SortOptionsGenerator into
// an in-Ruby sort, which loads every row — the bug this change is fixing.
const SORT_OPTIONS = ["trading_name", "sector"];

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
	property: any;
	isAdministrator?: boolean;
	// Workspace passes these to every tab.
	name?: string;
	icon?: string;
}

const PropertyCollection = ({ property, isAdministrator }: Props) => {
	const propertyId = _.get(property, "id");
	// null = the first load has not answered yet. An empty array is a legitimate
	// answer, and ListGroup renders the shared empty state for it.
	const [tenants, setTenants] = useState<any[] | null>(null);
	const [sortBy, setSortBy] = useState("trading_name");
	const [searchTerm, setSearchTerm] = useState("");
	const [query, setQuery] = useState("");
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(
		() =>
			new Datasource({
				mainModelName: "tenant",
				perPage: 20,
				currentPage: 1,
				sortBy: "trading_name",
				renderTrigger: renderTrigger
			})
	);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success) setTenants(response.normalizedMainModelResponse || []);
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	// DEBOUNCED WITH A setTimeout IN AN EFFECT, not `_.debounce`, and the same
	// shape as components/Projects/useProjectsSearch: this repo's jest runs with
	// legacy fake timers, which mock setTimeout but not Date.now — and lodash's
	// debounce compares the two, so under test it reschedules for ever and never
	// fires. `searchTerm` is what the box shows; `query` is what the API is asked
	// for, one debounce behind.
	useEffect(() => {
		const timer = setTimeout(() => {
			const nextQuery = searchTerm.trim();
			if (nextQuery === query) return;
			unstable_batchedUpdates(() => {
				// Page 4 of everything is rarely page 4 of the matches, and showing an
				// empty page reads as "no results".
				if (datasource.currentPage !== 1) datasource.setCurrentPage(1);
				setQuery(nextQuery);
			});
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchTerm, query, datasource]);

	useEffect(() => {
		// Held back for a non-administrator: the API authorizes this list
		// (PropertyPolicy#administrator?, via `authorize parent, :tenants?` in
		// V2::TenantsController#resolve_tenants) and the 403 that would land in the
		// console is not interesting, the sentence below is.
		if (!propertyId || !isAdministrator) return;
		datasource.get("v2", "tenants", {
			// Nils and blanks are OMITTED rather than sent: the query string is built
			// with URLSearchParams, which turns undefined into the literal string
			// "undefined".
			queryStringParameters: _.omitBy(
				{
					property_id: propertyId,
					sort: sortBy,
					per_page: datasource.perPage,
					page: datasource.currentPage,
					q: query.trim() || undefined
				},
				_.isNil
			)
		});
	}, [datasource, datasource.currentPage, datasource.perPage, propertyId, isAdministrator, sortBy, query]);

	// A LIST WITHHELD, NOT AN EMPTY ONE (Sim, 31 Aug 2026). `isAdministrator` is
	// membership of `property.users`, the same set PropertyPolicy#administrator?
	// checks server-side, computed once in Properties/Show. Without this the tab
	// said "No Tenants found", which reads as a property with no tenants.
	//
	// Declared AFTER the hooks above, never before: an early return that skips a
	// hook changes the hook order between renders.
	if (!isAdministrator) {
		return (
			<View title="Tenants">
				<EmptyState>
					<div>{translate("tenants_restricted_to_property_admin")}</div>
				</EmptyState>
			</View>
		);
	}

	const dash = (value: any) => (value === 0 || !!value ? value : "—");
	const nameOf = (tenant: any) => _.get(tenant, "trading_name") || _.get(tenant, "name") || "—";

	const onSortChange = (value: string) => {
		datasource.setCurrentPage(1);
		setSortBy(value);
	};

	const onSearchChange = (value: string) => setSearchTerm(value);

	return (
		<View title="Tenants">
			<ListGroupControl
				onSortChange={onSortChange}
				selectedSort={sortBy}
				sortOptions={SORT_OPTIONS}
				onSearchChange={onSearchChange}
				searchValue={searchTerm}
				searchLabel="search_tenants"
			/>
			{tenants === null ? (
				<LoadingState />
			) : (
				<React.Fragment>
					<ListGroup
						resource="tenants"
						// ListGroup's own client-side paginator is OFF: it would page within
						// the 20 rows this request returned and present that as the whole
						// set. The one below pages the API.
						pagination={false}
						collection={tenants}
						renderRow={(tenant: any) => (
							<ListGroupItem
								id={tenant.id}
								key={tenant.id}
								heading={nameOf(tenant)}
								datapoints={[
									{ key: "sector", value: <React.Fragment>{dash(_.get(tenant, "sector"))}</React.Fragment> },
									{ key: "contact_name", value: <React.Fragment>{dash(_.get(tenant, "contact_name"))}</React.Fragment> },
									{
										key: "contact_email",
										value: <React.Fragment>{dash(_.get(tenant, "contact_email"))}</React.Fragment>
									},
									{
										key: "phone_number",
										value: <React.Fragment>{dash(_.get(tenant, "phone_number"))}</React.Fragment>
									},
									{
										key: "",
										value: (
											<Link className="btn btn-primary" to={{ pathname: `/tenants/${tenant.id}/edit` }}>
												<i className="fa fa-pencil" /> Edit
											</Link>
										)
									}
								]}
							/>
						)}
					/>
					{!_.isEmpty(tenants) && (
						<div className={classNames("margin-auto", "flex justify-center")}>
							<Pagination datasource={datasource} />
						</div>
					)}
				</React.Fragment>
			)}
		</View>
	);
};

export default memo(PropertyCollection);
